/**
 * Voice Memo Document List Component
 * Shows list of documents in preview panel (like Basecamp work specs)
 */

import { ChevronRight, FileText, Brain } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { VoiceMemo } from './types';

interface DocumentItem {
  id: string;
  title: string;
  type: 'transcript' | 'analysis';
  memoId: string;
  memo: VoiceMemo;
}

interface VoiceMemoDocumentListProps {
  documents: DocumentItem[];
  selectedDocumentId: string | null;
  onSelectDocument: (document: DocumentItem) => void;
  type: 'transcript' | 'analysis';
}

export function VoiceMemoDocumentList({
  documents,
  selectedDocumentId,
  onSelectDocument,
  type
}: VoiceMemoDocumentListProps) {
  if (documents.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-sm text-muted-foreground">No {type}s found</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-border">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {type === 'transcript' ? 'TRANSCRIPTS' : 'ANALYSIS'} {documents.length}
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-2 space-y-1">
          {documents.map(doc => {
            const isSelected = selectedDocumentId === doc.id;

            return (
              <button
                key={doc.id}
                onClick={() => onSelectDocument(doc)}
                className={`w-full text-left p-3 rounded-md transition-colors ${
                  isSelected
                    ? 'bg-primary/10 border border-primary/20'
                    : 'hover:bg-muted/50 border border-transparent'
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {type === 'transcript' ? (
                      <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    ) : (
                      <Brain className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    )}
                    <h3 className="text-sm font-medium truncate">
                      {doc.title}
                    </h3>
                  </div>
                  <ChevronRight
                    className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${
                      isSelected ? 'text-primary' : ''
                    }`}
                  />
                </div>

                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{doc.memo.date}</span>
                  <span>•</span>
                  <span>{doc.memo.duration}</span>
                  {doc.memo.theme && (
                    <>
                      <span>•</span>
                      <Badge variant="outline" className="text-xs">{doc.memo.theme}</Badge>
                    </>
                  )}
                </div>

                {doc.memo.summary && type === 'analysis' && (
                  <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                    {doc.memo.summary}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
